import React, { useEffect, useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { supabase } from '../../supabaseClient';

const HRDetails = () => {
  const [match, params] = useRoute('/hr-details/:hrId');
  const [, navigate] = useLocation();
  const [hr, setHR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // For the dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    phone: '',
    email: '',
  });

  const hrId = params?.hrId;

  useEffect(() => {
    const fetchHRDetails = async () => {
      if (!hrId) {
        setError('HR ID is missing.');
        setLoading(false);
        return;
      }

      try {
        const { data: hrData, error: hrError } = await supabase
          .from('hr')
          .select('*')
          .eq('id', hrId)
          .single();

        if (hrError) {
          setError(hrError.message);
        } else {
          setHR(hrData);
          setFormValues({
            name: hrData.name || '',
            phone: hrData.phone || '',
            email: hrData.email || '',
          });
        }
      } catch (err) {
        setError('Error fetching HR details.');
      } finally {
        setLoading(false);
      }
    };

    fetchHRDetails();
  }, [hrId]);

  const handleBack = () => window.history.back();

  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleUpdate = async () => {
    try {
      const { data, error } = await supabase
        .from('hr')
        .update(formValues)
        .eq('id', hrId)
        .select();

      if (error) {
        setError(error.message);
      } else {
        setHR(data[0]);
        handleDialogClose();
      }
    } catch (err) {
      setError('Error updating HR details.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!hr) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>No HR details found.</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          HR Details
        </Typography>
        <Box>
         
          <Button
            variant="outlined"
            onClick={handleDialogOpen}
            sx={{
              marginLeft: 2,
              backgroundColor: 'black',
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              },
              mr : 1,
            }}
          >
            Update
          </Button>
          <Button
            variant="contained"
            onClick={handleBack}
            sx={{
              backgroundColor: 'black',
              color: 'white',
              '&:hover': {
                backgroundColor: '#333',
              },
            }}
          >
            Back
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            {hr?.name}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Contact Phone:
              </Typography>
              <Typography variant="body1">{hr.phone || 'Phone number not available'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" color="textPrimary" sx={{ fontWeight: 'bold' }}>
                Contact Email:
              </Typography>
              <Typography variant="body1">{hr.email || 'Email not available'}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Dialog for updating HR details */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Edit HR Details</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            value={formValues.name}
            onChange={handleInputChange}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Phone"
            name="phone"
            value={formValues.phone}
            onChange={handleInputChange}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Email"
            name="email"
            value={formValues.email}
            onChange={handleInputChange}
            fullWidth
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary"
           sx={{
            backgroundColor: 'black',
            color: 'white',
            '&:hover': {
              backgroundColor: '#333',
            },
            mr: 1,
          }}>
            Cancel
          </Button>
          <Button onClick={handleUpdate} color="primary"
           sx={{
            backgroundColor: 'black',
            color: 'white',
            '&:hover': {
              backgroundColor: '#333',
            },
            mr: 1,
          }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HRDetails;
